import { useDebounce } from 'hooks/useDebounce'
import React from 'react'
import { Loading, useGetBusinessID } from '@marketing-milk/frontend'
import { PublishCampaignDTO } from '@marketing-milk/interfaces'
import { Button, Drawer, IconButton, Tooltip } from '@material-ui/core'
import RefreshIcon from '@material-ui/icons/Refresh'
import { withStyles } from '@material-ui/styles'
import { CircularProgress, Skeleton } from '@mui/material'
import { campaignService } from 'app/services'
import { getApiErrorMessage } from 'app/util'
import { MMAlert } from 'components/Alert'
import { FullPageContent } from 'components/layout/FullPageLayout/Content/FullPageContent'
import { ListPageBar, PageBarFilters } from 'components/layout/PageBar'
import { UserLayout } from 'components/layout/UserLayout'
import { useFetchAllCampaigns } from 'hooks'
import { useFetchAllAudiences } from 'hooks/audiences/useFetchAllAudiences'
import { isEmpty, range } from 'lodash'
import { useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import { ConfirmationModal } from '../../../components/ConfirmationModal'
import { CampaignsContent } from './components/campaign-page/CampaignContent'
import { CampaignFiltersPanel } from './components/campaign-page/CampaignFiltersPanel'
import { CampaignForm } from './forms/CampaignForm'
import { canUploadCampaigns, getCampaignErrorMessage } from './helpers/campaign.helpers'

export const ErrorToolTip = withStyles(theme => ({
  tooltip: {
    fontSize: 14,
    background: 'darkred',
    padding: '10px',
  },
}))(Tooltip)

const DEFAULT_SORT_PROP = 'createdTime'
const DEFAULT_SORT_DIRECTION = 'DESC'

export const Campaigns = () => {
  const businessID = useGetBusinessID()
  const [searchTerm, setSearchInput] = useState('')
  const { debouncedValue: debouncedSearch } = useDebounce(searchTerm)
  const { audiences = [] } = useFetchAllAudiences(businessID)
  const [createOpen, setCreateOpen] = useState(false)
  const [creatingCampaign, setCreatingCampaign] = useState(false)

  const [deletingCampaign, setDeletingCampaign] = useState(false)
  const [campaignIDBeingDeleted, setCampaignIDBeingDeleted] = useState<number>()
  const [isRefreshing, setIsRefreshing] = useState(false)
  const bodyRef = useRef<HTMLDivElement>(null)
  const {
    campaigns,
    deleteCampaign,
    refetch,
    count,
    isLoading,
    error,
    fetchNextPage,
    current,
    isFetchingNextPage,
    setSortDirection,
    setSortProp,
    sortDirection,
    sortProp,
    setSearchTerm,
  } = useFetchAllCampaigns(Number(businessID))
  useEffect(() => {
    setSearchTerm(debouncedSearch)
  }, [debouncedSearch, setSearchTerm])

  const onCreateCampaign = async (campaignDto?: PublishCampaignDTO) => {
    setCreatingCampaign(true)
    if (!campaignDto) return toast.error('No DTO found')

    toast.success('We are submitting your campaign to facebook....')

    campaignService
      .publishCampaign(Number(businessID), campaignDto)
      .then(() => {
        refetch()
        setCreatingCampaign(false)
        toast.success('Campaign has been created')
      })
      .catch(err => {
        toast.error(`Campaign could not be published: ${getApiErrorMessage(err)}`)
        setCreatingCampaign(false)
      })

    setCreateOpen(false)
  }

  const onRefreshCampaigns = async () => {
    setIsRefreshing(true)
    toast.info(
      'Your campaigns, adsets, and ads are being synchronized with Facebook. This may take a few minutes.'
    )
    try {
      await campaignService.refreshCampaignsAndTheirChildren(Number(businessID))
      toast.success('Your campaigns, adsets, and ads have been synchronized successfully')
      refetch()
    } catch (e) {
      toast.error(`There was an error while synchronizing Campaigns: ${getApiErrorMessage(e)}`)
    }
    setIsRefreshing(false)
  }

  const onDeleteCampaign = async (campaignId: number) => {
    setCampaignIDBeingDeleted(campaignId)
    setDeletingCampaign(true)
  }

  useEffect(() => {
    if (!bodyRef.current) return
  }, [])

  const canUpload = canUploadCampaigns(audiences)
  const campaignUploadErrorMessages = getCampaignErrorMessage(audiences)

  return (
    <UserLayout>
      <ListPageBar
        subject="Campaigns"
        search={{ value: searchTerm, onChange: setSearchInput, placeholder: 'Search campaigns' }}
        filters={
          <PageBarFilters
            activeCount={
              sortProp !== DEFAULT_SORT_PROP || sortDirection !== DEFAULT_SORT_DIRECTION ? 1 : 0
            }
            onClear={() => {
              setSortProp(DEFAULT_SORT_PROP)
              setSortDirection(DEFAULT_SORT_DIRECTION)
            }}
          >
            <CampaignFiltersPanel
              sortProp={sortProp}
              setSortProp={setSortProp}
              sortDirection={sortDirection}
              setSortDirection={setSortDirection}
            />
          </PageBarFilters>
        }
        actions={
          <ErrorToolTip title={campaignUploadErrorMessages ?? ''}>
            <div className="flex items-center gap-2">
              <Button
                variant="contained"
                color="primary"
                disabled={!canUpload}
                onClick={() => setCreateOpen(true)}
              >
                Create A Campaign
              </Button>
              <IconButton onClick={onRefreshCampaigns} disabled={isRefreshing} title="Refresh">
                {!isRefreshing ? <RefreshIcon /> : <CircularProgress size={20} />}
              </IconButton>
            </div>
          </ErrorToolTip>
        }
        status={campaigns && !isLoading ? `Showing ${current} of ${count} campaigns` : undefined}
      />

      <Drawer open={createOpen} onClose={() => setCreateOpen(false)}>
        <CampaignForm audiences={audiences} onSubmit={onCreateCampaign} />
      </Drawer>

      <FullPageContent
        className="p-2"
        ref={bodyRef}
        onScroll={() => {
          if (isFetchingNextPage) return
          if (current === count) return
          if (!bodyRef.current) return
          const { scrollHeight, scrollTop, clientHeight } = bodyRef.current

          const currentPosition = scrollTop + clientHeight
          const positionToReach = scrollHeight - 100
          if (currentPosition > positionToReach) fetchNextPage()
        }}
      >
        <div className="flex-grow">
          {creatingCampaign && (
            <Loading loadingMessage="Creating New Campaign. This may take a few moments." />
          )}
          {error && (
            <div className="m-4 border rounded-md">
              <MMAlert type="error">
                <p className="w-full">{error[0]}</p>
              </MMAlert>
            </div>
          )}
          {isLoading &&
            range(0, 20).map(i => (
              <div key={i} className="w-full flex items-center justify-center pt-0.5">
                <Skeleton variant="rectangular" height={90} width={'100%'} />
              </div>
            ))}

          {!isLoading && (
            <CampaignsContent
              campaigns={campaigns}
              refetch={refetch}
              audiences={audiences}
              onDeleteCampaign={onDeleteCampaign}
            />
          )}

          <ConfirmationModal
            openState={[deletingCampaign, setDeletingCampaign]}
            onConfirm={() => {
              if (campaignIDBeingDeleted) deleteCampaign(campaignIDBeingDeleted)
            }}
            warningText="Are you sure you would like to delete this campaign? It will be removed from Facebook and all reporting data will be lost."
            confirmButtonText="Delete"
            confirmButtonType="delete"
          ></ConfirmationModal>

          <div className="w-full flex items-center justify-center pt-0.5">
            {/* We add this because on larger screens, only 8 campaigns might not fill the entire dom.  Without the dom being filled, the scroll handler will never be called.  After 15 campaigns are loaded, we know that any screen will be filled */}
            {/* The page bar now reports "showing N of N", so the button has to
                disappear once everything is loaded rather than contradict it. */}
            {!isEmpty(campaigns) && (campaigns || []).length < 15 && (current ?? 0) < count && (
              <div className="p-4">
                <Button onClick={() => fetchNextPage()} variant="contained" color="secondary">
                  Load More Campaigns
                </Button>
              </div>
            )}
            {isFetchingNextPage && <CircularProgress size={50} />}
          </div>
        </div>
      </FullPageContent>
    </UserLayout>
  )
}
